import { useEffect, useRef, useState } from 'react';
import { Briefcase, Mail, MapPin, Phone, Upload, UserRound } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useUserAvatar } from '../auth/UserAvatarContext';
import { devAuthEnabled } from '../lib/env';
import { api, ApiRequestError } from '../lib/api';
import { fetchGraphProfile, type GraphProfile } from '../lib/graph';
import { PageHeader } from '../components/shell/PageHeader';
import { menuIcons } from '../components/shell/menuIcons';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { AppearanceCard } from '../features/settings/AppearanceCard';
import { VoiceCard } from '../features/settings/VoiceCard';

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

export function ProfilePage() {
  const { principal, refresh } = useAuth();
  const { photoUrl, refresh: refreshAvatar } = useUserAvatar();
  const user = principal?.user;

  const [graph, setGraph] = useState<GraphProfile | null>(null);
  const [graphLoading, setGraphLoading] = useState(!devAuthEnabled);

  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pull the read-only Microsoft 365 profile details via Graph (skipped in
  // dev-auth mode). The avatar image itself is resolved + shared by the
  // UserAvatarProvider so the Topbar and this page stay in sync.
  useEffect(() => {
    if (devAuthEnabled) return;
    let cancelled = false;
    const controller = new AbortController();

    setGraphLoading(true);
    void (async () => {
      const profile = await fetchGraphProfile(controller.signal);
      if (cancelled) return;
      setGraph(profile);
      setGraphLoading(false);
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const onPickFile = () => {
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setUploadError('Please choose an image file.');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setUploadError('Image must be 5MB or smaller.');
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      await api.uploadAvatar(file);
      await refresh();
      refreshAvatar();
    } catch (err) {
      setUploadError(
        err instanceof ApiRequestError ? err.message : 'Could not upload the picture.',
      );
    } finally {
      setUploading(false);
    }
  };

  const name = user?.displayName ?? graph?.displayName ?? 'Account';
  const email = user?.email ?? graph?.mail ?? graph?.userPrincipalName ?? null;
  const jobTitle = graph?.jobTitle ?? user?.jobTitle ?? null;

  return (
    <>
      <PageHeader
        title="My Profile"
        icon={menuIcons.profile}
        description="Your Microsoft 365 details and the picture used across Smorgas Social."
      />

      {/* items-start: the right column grows with the Appearance card, and the picture card keeps
          its own height instead of stretching to match it. */}
      <div className="grid items-start gap-4 lg:grid-cols-3">
        {/* Profile picture + upload */}
        <Card className="lg:col-span-1">
          <CardHeader
            title="Profile picture"
            description="Used in the top bar and throughout the app."
          />
          <CardBody className="flex flex-col items-center gap-4 py-6">
            <Avatar name={name} size="lg" fetchCustom={false} photoUrl={photoUrl} />

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={onFileChange}
            />
            <Button
              variant="secondary"
              size="sm"
              loading={uploading}
              onClick={onPickFile}
              className="gap-2"
            >
              {!uploading && <Upload className="h-4 w-4" />}
              Upload a custom picture
            </Button>
            <p className="text-center text-xs text-ink-faint">
              PNG or JPG, up to 5MB. Falls back to your Microsoft 365 photo, then your initials.
            </p>
            {uploadError && <p className="text-center text-xs text-red-600 dark:text-error-dark">{uploadError}</p>}
          </CardBody>
        </Card>

        {/* The wider right column: the read-only M365 info, then Appearance. min-w-0 lets the column
            shrink inside its track, so a long email truncates instead of widening the page. */}
        <div className="flex min-w-0 flex-col gap-[1.125rem] lg:col-span-2">
          <Card>
            <CardHeader
              title="Microsoft 365"
              description={
                devAuthEnabled
                  ? 'Dev-auth mode — showing the seeded account; Microsoft 365 details are unavailable.'
                  : 'Read-only details synced from your Microsoft 365 account.'
              }
            />
            <CardBody>
              {graphLoading ? (
                <p className="text-sm text-ink-faint">Loading Microsoft 365 details…</p>
              ) : (
                <dl className="grid gap-4 sm:grid-cols-2">
                  <Detail
                    icon={<UserRound className="h-4 w-4" />}
                    label="Display name"
                    value={name}
                  />
                  <Detail icon={<Mail className="h-4 w-4" />} label="Email" value={email} />
                  <Detail
                    icon={<Briefcase className="h-4 w-4" />}
                    label="Job title"
                    value={jobTitle}
                  />
                  <Detail
                    icon={<MapPin className="h-4 w-4" />}
                    label="Office"
                    value={graph?.officeLocation ?? null}
                  />
                  <Detail
                    icon={<Phone className="h-4 w-4" />}
                    label="Mobile"
                    value={graph?.mobilePhone ?? null}
                  />
                </dl>
              )}
            </CardBody>
          </Card>

          {/* Personal Voice: moved here from App Settings, which is for workspace-wide
              configuration. Above Appearance deliberately — this is a substantive setting and
              Appearance is a light/dark toggle, so it stays last. Keyed on the current value so
              the card re-seeds its initial state after a save refreshes the principal. */}
          <VoiceCard
            key={`voice-${user?.personalVoice ?? ''}`}
            initialPersonal={user?.personalVoice ?? ''}
            onSaved={refresh}
          />

          <AppearanceCard />
        </div>
      </div>
    </>
  );
}

function Detail({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 text-ink-faint">{icon}</span>
      <div className="min-w-0">
        <dt className="text-xs font-medium uppercase tracking-wide text-ink-faint">{label}</dt>
        <dd className="mt-0.5 truncate text-sm text-ink-strong">
          {value ?? <span className="text-ink-faint">Not set</span>}
        </dd>
      </div>
    </div>
  );
}
